import { ICellEditorAngularComp } from '@ag-grid-community/angular';
import { ICellEditorParams } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { isEqual } from 'lodash-es';

import { PivotSettings } from '@models/trial-attributes/trial-attributes';

import { ZERO_HYPHEN } from '@shared/constants/format';
import { listBudgetAttributesQuery } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';

import {
  amountTypeMap,
  getPivotColumnValues,
} from '../adjustments-to-prepaid/grid-config/prepaid-impact-column-group';

interface AgPivotColumnCellEditorParams extends ICellEditorParams {
  budgetAttributes: listBudgetAttributesQuery[];
  isPivotSelected: boolean;
  pivotSettings: PivotSettings;
}

type AgPivotColumnDropdownOption = {
  label: string;
  value: string[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, FormsModule, ReactiveFormsModule],
  styleUrl: 'ag-pivot-column-dropdown.component.scss',
  templateUrl: 'ag-pivot-column-dropdown.component.html',
})
export class AgPivotColumnDropdownComponent implements ICellEditorAngularComp {
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);

  pivotValue = signal<string[]>([]);

  select = viewChild.required<NgSelectComponent>('select');

  options: AgPivotColumnDropdownOption[] = [];
  params!: AgPivotColumnCellEditorParams;
  zeroHyphen = ZERO_HYPHEN;

  agInit(params: AgPivotColumnCellEditorParams): void {
    this.params = params;

    const budgetAttributes = params.budgetAttributes || [];
    const isPivotSelected = params.isPivotSelected || false;
    const pivotSettings = params.pivotSettings || {};

    // Use getPivotColumnValues for options
    const values = getPivotColumnValues(isPivotSelected, budgetAttributes, pivotSettings);

    this.options = values.map((arr: string[]) => ({
      label: !arr.length
        ? ''
        : !isPivotSelected
          ? amountTypeMap[arr[0] as keyof typeof amountTypeMap] || arr[0]
          : arr.join(' / '),
      value: arr,
    }));

    // Ensure the value is always an array
    let value = params.value;

    if (Array.isArray(value)) {
      this.pivotValue.set(value);
    } else if (typeof value === 'string' && value) {
      try {
        const parsed = JSON.parse(value);

        this.pivotValue.set(Array.isArray(parsed) ? parsed : [parsed]);
      } catch {
        this.pivotValue.set([value]);
      }
    } else {
      this.pivotValue.set([]);
    }

    setTimeout(() => this.select().open());
  }

  compareArrays(a: Option<string[]>, b: string[]): boolean {
    const arrayA = a?.value || [];
    const arrayB = b || [];

    return isEqual(arrayA, arrayB);
  }

  getValue(): string[] {
    return this.pivotValue();
  }

  refresh(): boolean {
    return false;
  }

  searchFn(term: string, item: AgPivotColumnDropdownOption): boolean {
    return item.label.toLowerCase().includes(term.toLowerCase());
  }
}
